import React, { useState } from "react";
import { useStateValue } from "../../../State";

import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import SwipeableViews from "react-swipeable-views";
import MobileStepper from "@mui/material/MobileStepper";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";

import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Edit from "@mui/icons-material/EditRounded";
import Check from "@mui/icons-material/Check";
import Undo from "@mui/icons-material/Undo";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

import Languages from "../../../translations";
import { containsMoreThanEmptySpaces } from "../../../utils";

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  return (
    d.toLocaleDateString("fi-FI") +
    " " +
    d.toLocaleTimeString("fi-FI", { hour: "2-digit", minute: "2-digit" })
  );
};

const ReadonlyEntries = ({ entries, bgColor }) => {
  if (!entries || entries.length === 0) return null;
  return entries.map((entry, idx) => (
    <Box
      key={idx}
      sx={{
        backgroundColor: bgColor || "#a6dee0",
        borderRadius: 2,
        boxShadow: 1,
        mt: 1,
        pt: 1.5,
        pb: 1.5,
        px: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 0.5,
          ml: 1,
          mr: 1,
        }}
      >
        <Typography sx={{ fontWeight: "bold", fontSize: 13, color: "#424242" }}>
          {entry.PersonName}
        </Typography>
        {entry.Saved && (
          <Typography variant="caption" color="text.secondary">
            {formatDate(entry.Saved)}
          </Typography>
        )}
      </Box>
      <Typography variant="body1" sx={{ color: "black", mb: 2, ml: 1 }}>
        {entry.Text}
      </Typography>
    </Box>
  ));
};

const LearningAreaDetail = ({
  SelectedSlideIndex,
  sortedAreas,
  sectionTitle,
}) => {
  const { state, actions } = useStateValue();
  const MaxSlideCount = sortedAreas.length;
  const [SlideIndex, setSlideIndex] = useState(SelectedSlideIndex);
  const area = sortedAreas[SlideIndex];
  const planId = state.combinedPlan?.data?.PlanId;
  const isLocked = state.combinedPlan?.data?.Locked === true;
  const isReadOnly = isLocked;
  const isSaving =
    state.combinedPlan?.saveState === "SAVING_COMBINED_PLAN_LEARNING_AREA";
  const savedOk =
    state.combinedPlan?.saveState === "COMBINED_PLAN_LEARNING_AREA_SAVED_OK";
  const savedFailed =
    state.combinedPlan?.saveState ===
    "COMBINED_PLAN_LEARNING_AREA_SAVED_FAILED";

  // Parent perspective editing state
  const [editingParent, setEditingParent] = useState(false);
  const [parentText, setParentText] = useState(area?.Parent ?? "");
  const [showParentSaveBtn, setShowParentSaveBtn] = useState(false);

  // Child perspective editing state
  const [editingChild, setEditingChild] = useState(false);
  const [childText, setChildText] = useState(area?.Child ?? "");
  const [showChildSaveBtn, setShowChildSaveBtn] = useState(false);

  // Track which field was last saved for feedback
  const [lastSavedField, setLastSavedField] = useState(null);

  // Reset local state when the selected area changes
  React.useEffect(() => {
    setEditingParent(false);
    setParentText(area?.Parent ?? "");
    setShowParentSaveBtn(false);
    setEditingChild(false);
    setChildText(area?.Child ?? "");
    setShowChildSaveBtn(false);
    setLastSavedField(null);
  }, [SlideIndex, area]);

  // Keep the slide in sync if the entry index changes
  React.useEffect(() => {
    setSlideIndex(SelectedSlideIndex);
  }, [SelectedSlideIndex]);

  const handleChangeIndex = (index) => setSlideIndex(index);
  const handlePrev = () => {
    if (SlideIndex > 0) setSlideIndex(SlideIndex - 1);
  };
  const handleNext = () => {
    if (SlideIndex < MaxSlideCount - 1) setSlideIndex(SlideIndex + 1);
  };

  if (!area) return null;

  // Employee and VEO are always shown as read-only
  const readOnlyPerspectives = [
    {
      label: Languages.StaffNotes,
      value: area.Employee,
      saved: area.EmployeeSaved,
    },
    {
      label: Languages.VEONotes,
      value: area.Veo,
      saved: area.VeoSaved,
    },
  ];

  // Parent handlers
  const handleParentTextChange = (e) => {
    const val = e.target.value;
    setShowParentSaveBtn(
      containsMoreThanEmptySpaces(val) || (area.Parent && area.Parent !== ""),
    );
    setParentText(val);
  };

  const handleParentSave = () => {
    setLastSavedField("parent");
    if (area.QuestionId) {
      actions.triggerSaveCombinedPlanCustomQuestion({
        planId,
        areaListIndex: SlideIndex,
        questionId: area.QuestionId,
        field: "parent",
        text: parentText,
      });
    } else {
      actions.triggerSaveCombinedPlanLearningArea({
        planId,
        areaIndex: area.SortOrder,
        areaListIndex: SlideIndex,
        text: parentText,
      });
    }
    setEditingParent(false);
    setShowParentSaveBtn(false);
  };

  const handleParentCancel = () => {
    setParentText(area?.Parent ?? "");
    setShowParentSaveBtn(false);
    setEditingParent(false);
  };

  // Child perspective handlers
  const handleChildTextChange = (e) => {
    const val = e.target.value;
    setShowChildSaveBtn(
      containsMoreThanEmptySpaces(val) || (area.Child && area.Child !== ""),
    );
    setChildText(val);
  };

  const handleChildSave = () => {
    setLastSavedField("child");
    if (area.QuestionId) {
      actions.triggerSaveCombinedPlanCustomQuestion({
        planId,
        areaListIndex: SlideIndex,
        questionId: area.QuestionId,
        field: "child",
        text: childText,
      });
    } else {
      actions.triggerSaveCombinedPlanChildPerspective({
        planId,
        areaIndex: area.SortOrder,
        areaListIndex: SlideIndex,
        text: childText,
      });
    }
    setEditingChild(false);
    setShowChildSaveBtn(false);
  };

  const handleChildCancel = () => {
    setChildText(area?.Child ?? "");
    setShowChildSaveBtn(false);
    setEditingChild(false);
  };

  const renderEditableField = ({
    bgColor,
    fieldName,
    text,
    setText,
    editing,
    setEditing,
    showSaveBtn,
    handleTextChange,
    handleSave,
    handleCancel,
    savedField,
    savedTimestamp,
  }) => (
    <Box
      sx={{
        backgroundColor: bgColor,
        borderRadius: 2,
        boxShadow: 2,
        mt: 1,
        pt: 2,
        pb: 2,
        px: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          ml: 1,
          mr: 1,
          mb: 0.5,
        }}
      >
        <Typography
          color="textSecondary"
          sx={{
            fontWeight: "bold",
            fontSize: 14,
            color: "#424242",
          }}
        >
          {Languages.observations}
        </Typography>
        {savedTimestamp && (
          <Typography variant="caption" color="text.secondary">
            {savedTimestamp}
          </Typography>
        )}
      </Box>

      <TextField
        name={fieldName}
        multiline
        minRows={3}
        maxRows={7}
        fullWidth
        disabled={isReadOnly || !editing || isSaving}
        value={text}
        onChange={handleTextChange}
        onKeyDown={(e) => {
          if (e.keyCode === 13 && text.split(/\r\n|\r|\n/).length >= 15) {
            e.preventDefault();
          }
        }}
        inputProps={{ maxLength: 2400, style: { color: "black" } }}
        sx={{
          backgroundColor: "white",
          borderRadius: 2,
          "& .MuiInputBase-input.Mui-disabled": {
            WebkitTextFillColor: "black",
          },
        }}
        InputProps={{
          endAdornment: !isReadOnly ? (
            <InputAdornment position="start">
              {isSaving ? (
                <CircularProgress
                  size={20}
                  sx={{ position: "absolute", top: 8, right: 8 }}
                />
              ) : !editing ? (
                <IconButton
                  sx={{ position: "absolute", top: 5, right: 5 }}
                  onClick={() => setEditing(true)}
                  size="medium"
                >
                  <Edit />
                </IconButton>
              ) : (
                <IconButton
                  sx={{ position: "absolute", top: 5, right: 5 }}
                  onClick={handleCancel}
                  size="medium"
                >
                  <Undo />
                </IconButton>
              )}
              {showSaveBtn && editing && !isSaving && (
                <IconButton
                  sx={{ position: "absolute", top: 5, right: 45 }}
                  onClick={handleSave}
                  size="medium"
                >
                  <Check />
                </IconButton>
              )}
              <Typography
                sx={{
                  fontSize: 12,
                  color: "#424242",
                  position: "absolute",
                  bottom: 5,
                  right: 10,
                }}
              >
                {text.length}/2400
              </Typography>
            </InputAdornment>
          ) : undefined,
        }}
      />

      {savedOk && !editing && lastSavedField === savedField && (
        <Box sx={{ display: "flex", alignItems: "center", mt: 1, ml: 1 }}>
          <CheckCircleOutlineIcon
            color="success"
            fontSize="small"
            sx={{ mr: 0.5 }}
          />
          <Typography variant="caption" color="success.main">
            Tallennettu
          </Typography>
        </Box>
      )}
      {savedFailed && !editing && lastSavedField === savedField && (
        <Typography
          variant="caption"
          color="error"
          sx={{ mt: 1, ml: 1, display: "block" }}
        >
          Tallennus epäonnistui. Yritä uudelleen.
        </Typography>
      )}
    </Box>
  );

  return (
    <div className="CombinedPlanLearningAreaDetail">
      <Box>
        <SwipeableViews
          className={"col-12 textCenter slide pt-3"}
          index={SlideIndex}
          key={SlideIndex}
          onChangeIndex={handleChangeIndex}
        >
          {sortedAreas.map((a, index) => (
            <div key={index}>
              <Box
                className="br-top col-12"
                sx={{ backgroundColor: "#a6dee0" }}
              >
                <Typography
                  color="textSecondary"
                  sx={{
                    textAlign: "center",
                    fontWeight: "Bold",
                    fontSize: 18,
                    color: "#424242",
                    pt: 1,
                  }}
                >
                  {a.Title}
                </Typography>
                <MobileStepper
                  style={{
                    backgroundColor: "#a6dee0",
                    color: "black",
                    padding: 0,
                  }}
                  variant="text"
                  steps={MaxSlideCount}
                  position="static"
                  activeStep={SlideIndex}
                  nextButton={
                    <Button
                      size="small"
                      style={{
                        color: "black",
                        opacity: SlideIndex === MaxSlideCount - 1 ? "0.5" : "",
                      }}
                      onClick={handleNext}
                      disabled={SlideIndex === MaxSlideCount - 1}
                    >
                      {Languages.next} <KeyboardArrowRight />
                    </Button>
                  }
                  backButton={
                    <Button
                      size="small"
                      style={{
                        color: "black",
                        opacity: SlideIndex === 0 ? "0.5" : "",
                      }}
                      onClick={handlePrev}
                      disabled={SlideIndex === 0}
                    >
                      <KeyboardArrowLeft /> {Languages.prev}
                    </Button>
                  }
                />
              </Box>
              <Box
                className="br-btm col-12"
                sx={{ boxShadow: 2, mb: 3, pb: 1, backgroundColor: "white" }}
              >
                <Typography
                  color="textSecondary"
                  sx={{
                    textAlign: "center",
                    fontWeight: "Medium",
                    fontSize: 16,
                    color: "#424242",
                    pt: 1,
                  }}
                >
                  {a.SubTitle}
                </Typography>
              </Box>
            </div>
          ))}
        </SwipeableViews>

        <Box sx={{ mt: 1, px: 1 }}>
          {/* Editable parent/guardian perspective */}
          <Divider textAlign="left" sx={{ mt: 2 }}>
            <Chip
              label={
                <Typography
                  sx={{ fontWeight: "bold", fontSize: 14, color: "#424242" }}
                >
                  Huoltajan näkemykset
                </Typography>
              }
              variant="outlined"
            />
          </Divider>
          {renderEditableField({
            bgColor: "#a6dee0",
            fieldName: "ParentNotes",
            text: parentText,
            setText: setParentText,
            editing: editingParent,
            setEditing: setEditingParent,
            showSaveBtn: showParentSaveBtn,
            handleTextChange: handleParentTextChange,
            handleSave: handleParentSave,
            handleCancel: handleParentCancel,
            savedField: "parent",
            savedTimestamp: area.ParentSaved,
          })}

          {/* Other guardians' and employees' parent perspective entries */}
          <ReadonlyEntries entries={area.ParentEntries} bgColor="#a6dee0" />

          {/* Editable child perspective */}
          <Divider textAlign="left" sx={{ mt: 2 }}>
            <Chip
              label={
                <Typography
                  sx={{ fontWeight: "bold", fontSize: 14, color: "#424242" }}
                >
                  Lapsen mielipide ja toiveet
                </Typography>
              }
              variant="outlined"
            />
          </Divider>
          {renderEditableField({
            bgColor: "#a6dee0",
            fieldName: "ChildNotes",
            text: childText,
            setText: setChildText,
            editing: editingChild,
            setEditing: setEditingChild,
            showSaveBtn: showChildSaveBtn,
            handleTextChange: handleChildTextChange,
            handleSave: handleChildSave,
            handleCancel: handleChildCancel,
            savedField: "child",
            savedTimestamp: area.ChildSaved,
          })}

          {/* Other guardians' and employees' child perspective entries */}
          <ReadonlyEntries entries={area.ChildEntries} bgColor="#a6dee0" />

          {/* Read-only perspectives (employee, VEO) */}
          {readOnlyPerspectives.map((perspective, idx) => (
            <React.Fragment key={idx}>
              <Divider textAlign="left" sx={{ mt: 2 }}>
                <Chip
                  label={
                    <Typography
                      sx={{
                        fontWeight: "bold",
                        fontSize: 14,
                        color: "#424242",
                      }}
                    >
                      {perspective.label}
                    </Typography>
                  }
                  variant="outlined"
                />
              </Divider>
              <Box
                sx={{
                  backgroundColor: "#a6dee0",
                  borderRadius: 2,
                  boxShadow: 1,
                  mt: 1,
                  pt: 1.5,
                  pb: 2,
                  px: 1,
                }}
              >
                {perspective.saved && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mr: 1,
                      mb: 0.5,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {perspective.saved}
                    </Typography>
                  </Box>
                )}
                <Typography
                  variant="body1"
                  sx={{ color: "black", mb: 2, ml: 1 }}
                >
                  {perspective.value}
                </Typography>
              </Box>
            </React.Fragment>
          ))}
        </Box>
      </Box>
    </div>
  );
};

export default LearningAreaDetail;
